import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";
import { Button } from "../components/ui/button";
import CounselingFormModal from "../components/CounselingFormModal";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const PAGE_SIZE = 6;

const COURSE_CATEGORIES = [
  { label: "All", keywords: [] },
  { label: "Diploma", keywords: ["gnm", "anm", "diploma", "d.n.m"] },
  {
    label: "Bachelor's",
    keywords: ["b.sc", "bca", "bsc", "bachelor", "post basic"],
  },
  {
    label: "Master's",
    keywords: ["m.sc", "msc", "master", "post graduate", "pg"],
  },
  { label: "Certificate", keywords: ["certificate", "certification"] },
];

export const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter across ALL courses (not just current page)
  const filteredCourses = useMemo(() => {
    const term = search.toLowerCase().trim();
    const cat = COURSE_CATEGORIES.find((c) => c.label === activeCategory);
    return courses.filter((course) => {
      const titleLower = course.title.toLowerCase();
      const matchesSearch =
        !term ||
        titleLower.includes(term) ||
        (course.description || "").toLowerCase().includes(term);
      const matchesCategory =
        cat.keywords.length === 0 ||
        cat.keywords.some((kw) => titleLower.includes(kw));
      return matchesSearch && matchesCategory;
    });
  }, [courses, search, activeCategory]);

  const totalPages = Math.ceil(filteredCourses.length / PAGE_SIZE);
  const pagedCourses = filteredCourses.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeCategory]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = "Our Courses - Smart Scholars 360";
    AOS.init({ duration: 800, once: false });
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      // Fetch all courses at once so search/filter works across the full list
      const res = await axios.get(
        `${BASE_URI}/entities?category=course&page=1&limit=1000`,
        getAuthHeaders(),
      );

      if (res.data && res.data.data) {
        const transformedData = res.data.data.map((item) => ({
          id: item.id,
          title: item.data.title,
          description: item.data.description,
          eligibility: item.data.eligibility,
          career_scope: item.data.career_scope,
          duration: item.data.duration,
          image:
            item.data.images && item.data.images.length > 0
              ? item.data.images[0]
              : null,
        }));
        setCourses(transformedData);
      } else {
        setCourses([]);
      }
      setError("");
    } catch (err) {
      setError("Failed to load courses. Please try again later.");
      setCourses([
        {
          id: 1,
          image: "/course1.jpeg",
          title: "General Nursing and Midwifery (GNM)",
          description:
            "A diploma program focused on nursing care, midwifery, and clinical skills for patient support.",
          eligibility:
            "10+2 pass with any stream (preferably Science); minimum age 17 years.",
          career_scope:
            "Eligible for staff nurse roles in hospitals, clinics, and can pursue Post Basic B.Sc. Nursing for further studies.",
        },
        {
          id: 2,
          image: "/course2.avif",
          title: "Bachelor of Science in Nursing (B.Sc Nursing)",
          description:
            "A 4-year degree course that trains students to become professional nurses capable of delivering comprehensive healthcare.",
          eligibility:
            "10+2 with Physics, Chemistry, Biology, and English; minimum 45% marks.",
          career_scope:
            "Registered Nurse, Hospital Staff Nurse, Nurse Educator; eligible for M.Sc Nursing and international nursing exams.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      document
        .querySelector(".Courses_body")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const CourseSkeleton = () => (
    <div className="col-lg-6 mb-3" style={{ opacity: 0.7 }}>
      <div className="course card">
        <div className="course_card_image_box">
          <div
            style={{
              width: "100%",
              height: "200px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
              borderRadius: "8px",
            }}
          />
        </div>
        <div className="course_card_text pt-5">
          <div
            style={{
              width: "80%",
              height: "24px",
              borderRadius: "4px",
              marginBottom: "15px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              style={{
                width: i === 2 ? "75%" : "100%",
                height: "14px",
                borderRadius: "4px",
                marginBottom: "6px",
                background:
                  "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite",
              }}
            />
          ))}
        </div>
        <div className="card-button text-center pt-2">
          <div
            style={{
              width: "90px",
              height: "32px",
              borderRadius: "4px",
              margin: "0 auto",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        </div>
      </div>
    </div>
  );

  if (loading && courses.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Explore Courses</h1>
            <p>Find the right program to shape your future</p>
          </div>
        </div>
        <section className="Courses_body">
          <div className="container">
            <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
            {/* Filter bar skeleton */}
            <div className="course-filter-bar" style={{ marginBottom: 36 }}>
              <div className="course-filter-row">
                <div
                  style={{
                    flex: 1,
                    height: 42,
                    borderRadius: 10,
                    background:
                      "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
                <div
                  style={{
                    width: 190,
                    height: 42,
                    borderRadius: 10,
                    background:
                      "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              </div>
              <div className="course-filter-meta">
                <div
                  style={{
                    width: 120,
                    height: 14,
                    borderRadius: 4,
                    background:
                      "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              </div>
            </div>
            {/* Course card skeletons */}
            <div className="row">
              {Array.from({ length: 6 }).map((_, i) => (
                <CourseSkeleton key={i} />
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  if (!loading && courses.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Explore Courses</h1>
            <p>Find the right program to shape your future</p>
          </div>
        </div>
        <section className="Courses_body">
          <div className="container">
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>No courses available</h3>
              <p>Check back later for new courses!</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  const filtersActive = search || activeCategory !== "All";

  return (
    <>
      <div className="ss-inner-hero">
        <div className="container">
          <h1>Explore Courses</h1>
          <p>Find the right program to shape your future</p>
        </div>
      </div>
      <section className="Courses_body">
        <div className="container">
          <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>

          {error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          {/* Filter bar: search + category dropdown */}
          {!loading && (
            <div className="course-filter-bar">
              <div className="course-filter-row">
                {/* Search */}
                <div className="course-search-box">
                  <i className="bi bi-search course-search-icon"></i>
                  <input
                    type="text"
                    className="form-control course-search-input"
                    placeholder="Search by course name or keyword..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      className="clear-btn"
                      onClick={() => setSearch("")}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                {/* Category filter */}
                <div className="course-category-wrapper">
                  <i className="bi bi-funnel course-filter-icon"></i>
                  <select
                    className="form-select course-category-select"
                    value={activeCategory}
                    onChange={(e) => setActiveCategory(e.target.value)}
                  >
                    {COURSE_CATEGORIES.map((cat) => (
                      <option key={cat.label} value={cat.label}>
                        {cat.label === "All" ? "All Categories" : cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="course-filter-meta">
                <span className="course-filter-count">
                  {filteredCourses.length} active course
                  {filteredCourses.length !== 1 ? "s" : ""}
                </span>
                {filtersActive && (
                  <button
                    className="course-filter-clear-link"
                    onClick={() => {
                      setSearch("");
                      setActiveCategory("All");
                    }}
                  >
                    <i className="bi bi-x-circle"></i> Clear filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* No results */}
          {!loading && filteredCourses.length === 0 && (
            <div className="text-center py-5">
              <p style={{ color: "#6b7280", fontSize: "16px" }}>
                No courses found
                {search && (
                  <>
                    {" "}
                    for "<b>{search}</b>"
                  </>
                )}
                {activeCategory !== "All" && (
                  <>
                    {" "}
                    in <b>{activeCategory}</b>
                  </>
                )}
                .
              </p>
            </div>
          )}

          {/* Course grid — current page of filtered results */}
          {!loading && pagedCourses.length > 0 && (
            <div className="row">
              {pagedCourses.map((course) => (
                <div className="col-lg-6 mb-3 d-flex" key={course.id}>
                  <div className="course card">
                    <div className="course_card_image_box">
                      <div
                        style={{
                          position: "relative",
                          display: "block",
                          width: "100%",
                          height: "100%",
                          borderRadius: "inherit",
                        }}
                      >
                        {course.image ? (
                          <>
                            <img
                              src={course.image}
                              alt={course.title}
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextElementSibling.style.display =
                                  "flex";
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "#f8f9fa",
                                display: "none",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px dashed #dee2e6",
                                color: "#6c757d",
                                fontSize: "14px",
                              }}
                            >
                              Image not available
                            </div>
                          </>
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "200px",
                              backgroundColor: "#f8f9fa",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px dashed #dee2e6",
                              color: "#6c757d",
                              fontSize: "14px",
                            }}
                          >
                            No image provided
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="course_card_text pt-5">
                      <h3>{course.title}</h3>
                      <p className="overview pt-3">
                        <b>Overview:</b> {course.description}
                      </p>
                      <p className="eligibility">
                        <b>Eligibility:</b> {course.eligibility}
                      </p>
                      <p className="career_scope">
                        <b>Career Scope:</b> {course.career_scope}
                      </p>
                      {course.duration && (
                        <p className="duration">
                          <b>Duration:</b> {course.duration}
                        </p>
                      )}
                    </div>
                    <div className="card-button text-center pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCourse(course.title);
                          setShowModal(true);
                        }}
                      >
                        Apply now
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination over filtered results */}
          {totalPages > 1 && (
            <div style={{ textAlign: "center", marginTop: "30px" }}>
              <nav aria-label="Courses pagination">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {generatePageNumbers(currentPage, totalPages).map(
                    (page, i) => (
                      <li
                        key={i}
                        className={`page-item ${page === currentPage ? "active" : ""} ${page === "..." ? "disabled" : ""}`}
                      >
                        {page === "..." ? (
                          <span className="page-link">...</span>
                        ) : (
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        )}
                      </li>
                    ),
                  )}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </section>
      <CounselingFormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={`Apply for ${selectedCourse}`}
      />
    </>
  );
};
